// Zuther: an operator's statement report from Companies > detail (#737): the
// same file the operator downloads from its portal, for any date range
// (South African time, both days in; default the 1st of this month to
// today), as Excel or PDF. It follows the operator's own "Show driver pay on
// statements" setting, so driver pages are refused while that is off.

import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { AdminCompanyAPI } from '../../services/api';

const SAST_MS = 2 * 60 * 60 * 1000;
export const sastToday = (now = new Date()) => new Date(now.getTime() + SAST_MS).toISOString().slice(0, 10);

function fileNameOf(res, fallback) {
  const match = /filename="([^"]+)"/.exec(res.headers?.['content-disposition'] || '');
  return match ? match[1] : fallback;
}

async function messageOf(e, fallback) {
  const data = e?.response?.data;
  if (data && typeof data.text === 'function') {
    try { return JSON.parse(await data.text()).message || fallback; } catch (err) { return fallback; }
  }
  return data?.message || fallback;
}

const CompanyStatementReport = ({ companyId }) => {
  const [from, setFrom] = useState(() => `${sastToday().slice(0, 8)}01`);
  const [to, setTo] = useState(() => sastToday());
  const [format, setFormat] = useState('pdf');
  const [driverPages, setDriverPages] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const valid = Boolean(from && to && from <= to);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const params = { from, to, format, ...(format === 'pdf' && driverPages ? { driverPages: '1' } : {}) };
      const res = await AdminCompanyAPI.downloadStatement(companyId, params);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileNameOf(res, `Zuttle-statement-${from}-to-${to}.${format}`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(await messageOf(e, 'Could not make the statement.'));
    } finally {
      setBusy(false);
    }
  };

  const input = 'block mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none';
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900">Statement report</h3>
      <p className="text-sm text-gray-500 mt-1">The operator's statement for any dates, South African time, both days in.</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-700">From
          <input type="date" aria-label="From" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={input} />
        </label>
        <label className="text-sm text-gray-700">To
          <input type="date" aria-label="To" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={input} />
        </label>
        <label className="text-sm text-gray-700">File
          <select aria-label="File type" value={format} onChange={(e) => setFormat(e.target.value)} className={input}>
            <option value="pdf">PDF</option>
            <option value="xlsx">Excel</option>
          </select>
        </label>
        {format === 'pdf' && (
          <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
            <input type="checkbox" checked={driverPages} onChange={(e) => setDriverPages(e.target.checked)} className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
            Include a page per driver
          </label>
        )}
        <button type="button" onClick={generate} disabled={!valid || busy}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium text-sm disabled:opacity-50">
          <Download size={16} />
          {busy ? 'Making file…' : 'Generate'}
        </button>
      </div>
      {!valid && <p className="mt-2 text-sm text-red-600">The start date must be on or before the end date.</p>}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default CompanyStatementReport;
